// src/EmailVerification.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './EmailVerification.css';
import AuthService from './AuthService';
import APIService from './APIService';

export default function EmailVerification({ email, onVerified, onBack, onResendCode }) {
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (verificationCode.length !== 6) {
        setError(t('emailVerification.invalidCode'));
        setLoading(false);
        return;
      }

      await APIService.confirmSignup(email, verificationCode)
      
      setSuccess(true);
      
      onVerified();
    } catch (err) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendDisabled) return;
    
    setLoading(true);
    setError('');

    try {
      await APIService.resendConfirmCode(email)
      
      // Start countdown
      setResendDisabled(true);
      setCountdown(60);
      
      if (onResendCode) {
        onResendCode();
      }
      
      // Show success notification
      alert(t('emailVerification.resentSuccess'));
    } catch (err) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    setError('');
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        
        <div className="auth-card email-verification-card">
          <div className="auth-header success-header">
            <div className="icon-wrapper success-icon">
              <svg className="header-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>{t('emailVerification.successTitle')}</h1>
            <p className="auth-subtitle">
              {t('emailVerification.successSubtitle')}
            </p>
          </div>
          
          <div className="verification-success-animation">
            <div className="success-checkmark">
              <svg viewBox="0 0 52 52">
                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>
      
      <div className="auth-card email-verification-card">
        <a href="#" className="back-button"
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('emailVerification.backToSignUp')}
        </a>

        <div className="auth-header">
          <div className="icon-wrapper">
            <svg className="header-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 7L12 13L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1>{t('emailVerification.title')}</h1>
          <p className="auth-subtitle">
            {t('emailVerification.subtitle', { email })}
          </p>
        </div>

        <form onSubmit={handleVerifySubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="16" r="1" fill="currentColor"/>
              </svg>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="verificationCode">{t('emailVerification.verificationCode')}</label>
            <div className="input-wrapper">
              <svg className="input-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                id="verificationCode"
                name="verificationCode"
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder="000000"
                required
                autoFocus
                maxLength="6"
                className="code-input"
              />
            </div>
            <p className="input-hint">{t('emailVerification.codeHint')}</p>
          </div>

          <div className="info-box">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <div className="info-content">
              <p><strong>{t('emailVerification.haventReceived')}</strong></p>
              <p>{t('emailVerification.checkSpam')}</p>
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? (
              <span className="loading-spinner"></span>
            ) : (
              <>
                {t('emailVerification.verifyBtn')}
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </>
            )}
          </button>

          <div className="resend-section">
            <span>{t('emailVerification.didntReceive')}</span>
            <a
              href="#"
              className={`resend-link ${resendDisabled || loading ? 'disabled' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                if (!resendDisabled && !loading) {
                  handleResendCode();
                }
              }}
            >
              {resendDisabled ? t('emailVerification.resendIn', { countdown }) : t('emailVerification.resendCode')}
            </a>
          </div>
        </form>

        <div className="verification-footer">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 15V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="9" r="1" fill="currentColor"/>
            <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p>{t('emailVerification.securityNote')}</p>
        </div>
      </div>
    </div>
  );
}